<div class="surface-ground min-h-screen p-4">
  <share-page-header
    [title]="currentUserName ? 'Welcome back, ' + currentUserName : 'Welcome back'"
    [subtitle]="
      currentRoleDescription || 'Here\'s an overview of what\'s applicable to your account today'
    "
  ></share-page-header>

  <!-- KPI Summary Row - only the cards relevant to what this role can see -->
  <div class="grid mb-4" *ngIf="canSeePortalStats || canSeeOperationsStats || canSeeCrmStats">
    <div class="col-12 sm:col-6 lg:col-3" *ngIf="canSeePortalStats">
      <share-stat-card
        icon="pi pi-users"
        label="Total Customers"
        color="blue"
        [value]="portalStats?.total_customers ?? 0"
        [loading]="portalStatsLoading"
      ></share-stat-card>
    </div>
    <div class="col-12 sm:col-6 lg:col-3" *ngIf="canSeePortalStats">
      <share-stat-card
        icon="pi pi-check-circle"
        label="Active Subscriptions"
        color="brand"
        [value]="portalStats?.total_active_subscriptions ?? 0"
        [loading]="portalStatsLoading"
      ></share-stat-card>
    </div>
    <div class="col-12 sm:col-6 lg:col-3" *ngIf="canSeeOperationsStats">
      <share-stat-card
        icon="pi pi-desktop"
        label="Total Devices"
        color="purple"
        [value]="deviceStats?.total ?? 0"
        [loading]="deviceStatsLoading"
      ></share-stat-card>
    </div>
    <div class="col-12 sm:col-6 lg:col-3" *ngIf="canSeeCrmStats">
      <share-stat-card
        icon="pi pi-briefcase"
        label="New Leads"
        color="orange"
        [value]="leadStats?.new ?? 0"
        [loading]="crmLoading"
      ></share-stat-card>
    </div>
  </div>

  <!-- Main content - full-width, stacked -->
  <share-section-card
    [title]="accessibleAreas.length ? 'Navigate To' : ''"
    [icon]="accessibleAreas.length ? 'pi pi-bolt' : ''"
  >
    <div class="flex flex-wrap gap-2" *ngIf="accessibleAreas.length; else noAreas">
      <button
        *ngFor="let area of accessibleAreas"
        pButton
        [label]="area.label"
        [icon]="area.icon"
        class="p-button-outlined p-button-sm"
        (click)="navigateTo([area.path])"
      ></button>
    </div>
    <ng-template #noAreas>
      <p class="text-600 text-sm m-0">
        No other areas are enabled for your account yet. If you were expecting access to
        something specific, contact your administrator.
      </p>
    </ng-template>
  </share-section-card>

  <p
    class="text-600 text-center py-6 m-0"
    *ngIf="!canSeePortalStats && !canSeeOperationsStats && !canSeeServicesStats && !canSeeCrmStats"
  >
    Nothing else is configured to show here for your role yet.
  </p>

  <share-section-card
    title="Overview"
    icon="pi pi-th-large"
    *ngIf="canSeePortalStats || canSeeOperationsStats || canSeeServicesStats || canSeeCrmStats"
  >
    <p-tabView>
      <p-tabPanel header="Portal" *ngIf="canSeePortalStats">
        <ng-container *ngIf="!portalStatsLoading; else statsSkeleton">
          <div class="grid" *ngIf="portalStats">
            <div class="col-12 md:col-6">
              <div class="surface-100 p-3 border-round border-1 border-200 h-full">
                <div class="flex align-items-center gap-2 mb-2">
                  <i class="pi pi-box text-600"></i>
                  <span class="text-600 text-sm">Available Packages</span>
                </div>
                <div class="text-900 font-bold text-xl">
                  {{ portalStats.total_available_packages ?? 0 }}
                </div>
              </div>
            </div>
            <div class="col-12 md:col-6">
              <div class="surface-100 p-3 border-round border-1 border-200 h-full">
                <div class="flex align-items-center gap-2 mb-2">
                  <i class="pi pi-percentage text-600"></i>
                  <span class="text-600 text-sm">Subscription Rate</span>
                </div>
                <div class="text-900 font-bold text-xl">
                  {{
                    (portalStats.total_customers
                      ? (portalStats.total_active_subscriptions / portalStats.total_customers) *
                        100
                      : 0
                    ) | number: '1.0-1'
                  }}%
                </div>
                <div class="text-500 text-xs mt-1">customers with an active subscription</div>
              </div>
            </div>
          </div>
          <p *ngIf="!portalStats" class="text-600 text-center py-3 m-0">
            No statistics available
          </p>
        </ng-container>
      </p-tabPanel>

      <p-tabPanel header="Operations" *ngIf="canSeeOperationsStats">
        <ng-container *ngIf="!deviceStatsLoading; else statsSkeleton">
          <div class="grid" *ngIf="deviceStats">
            <div class="col-12 sm:col-6 lg:col-2p4">
              <div
                class="flex justify-content-between align-items-center p-2 surface-50 border-round border-1 border-200"
              >
                <div class="flex align-items-center gap-2">
                  <i class="pi pi-desktop text-600"></i>
                  <span class="text-900 font-medium text-sm">Total</span>
                </div>
                <span class="font-bold text-900">{{ deviceStats.total ?? 0 }}</span>
              </div>
            </div>
            <div class="col-12 sm:col-6 lg:col-2p4">
              <div
                class="flex justify-content-between align-items-center p-2 surface-50 border-round border-1 border-200"
              >
                <div class="flex align-items-center gap-2">
                  <span class="status-dot bg-blue-500"></span>
                  <span class="text-900 font-medium text-sm">Configured</span>
                </div>
                <span class="font-bold text-blue-600">{{ deviceStats.configured ?? 0 }}</span>
              </div>
            </div>
            <div class="col-12 sm:col-6 lg:col-2p4">
              <div
                class="flex justify-content-between align-items-center p-2 surface-50 border-round border-1 border-200"
              >
                <div class="flex align-items-center gap-2">
                  <span class="status-dot bg-green-500"></span>
                  <span class="text-900 font-medium text-sm">Available</span>
                </div>
                <span class="font-bold text-green-600">{{ deviceStats.available ?? 0 }}</span>
              </div>
            </div>
            <div class="col-12 sm:col-6 lg:col-2p4">
              <div
                class="flex justify-content-between align-items-center p-2 surface-50 border-round border-1 border-200"
              >
                <div class="flex align-items-center gap-2">
                  <span class="status-dot bg-orange-400"></span>
                  <span class="text-900 font-medium text-sm">Billed</span>
                </div>
                <span class="font-bold text-orange-500">{{ deviceStats.billed ?? 0 }}</span>
              </div>
            </div>
            <div class="col-12 sm:col-6 lg:col-2p4">
              <div
                class="flex justify-content-between align-items-center p-2 surface-50 border-round border-1 border-200"
              >
                <div class="flex align-items-center gap-2">
                  <span class="status-dot bg-purple-400"></span>
                  <span class="text-900 font-medium text-sm">Notified</span>
                </div>
                <span class="font-bold text-purple-500">{{ deviceStats.notified ?? 0 }}</span>
              </div>
            </div>
          </div>
          <p *ngIf="!deviceStats" class="text-600 text-center py-3 m-0">
            No device data available
          </p>
        </ng-container>
      </p-tabPanel>

      <p-tabPanel header="Services" *ngIf="canSeeServicesStats">
        <ng-container *ngIf="!orderStatsLoading; else statsSkeleton">
          <div class="grid" *ngIf="orderStats; else noOrderStats">
            <div class="col-12 md:col-4" *ngIf="orderStats.total_orders_outstanding as stat">
              <div class="surface-100 p-3 border-round border-1 border-200 h-full">
                <div class="flex align-items-center gap-2 mb-2">
                  <i class="pi pi-wallet text-600"></i>
                  <span class="text-600 text-sm">Orders Outstanding</span>
                </div>
                <div class="text-900 font-bold text-xl mb-2">
                  {{ stat.current ?? 0 | currency: 'R' }}
                </div>
                <div class="flex align-items-center gap-1">
                  <i [ngClass]="getTrendIconClass(stat.trend)" class="text-sm"></i>
                  <span
                    class="text-sm font-medium"
                    [ngClass]="{
                      'text-green-500': stat.trend === 'up',
                      'text-pink-500': stat.trend === 'down',
                      'text-600': stat.trend !== 'up' && stat.trend !== 'down'
                    }"
                  >
                    {{ (stat.current ?? 0) - (stat.previous ?? 0) | currency: 'R' }}
                  </span>
                  <span class="text-500 text-xs">vs last period</span>
                </div>
              </div>
            </div>
            <div class="col-12 md:col-4" *ngIf="orderStats.total_mrc_outstanding as stat">
              <div class="surface-100 p-3 border-round border-1 border-200 h-full">
                <div class="flex align-items-center gap-2 mb-2">
                  <i class="pi pi-sync text-600"></i>
                  <span class="text-600 text-sm">MRC Outstanding</span>
                </div>
                <div class="text-900 font-bold text-xl mb-2">
                  {{ stat.current ?? 0 | currency: 'R' }}
                </div>
                <div class="flex align-items-center gap-1">
                  <i [ngClass]="getTrendIconClass(stat.trend)" class="text-sm"></i>
                  <span
                    class="text-sm font-medium"
                    [ngClass]="{
                      'text-green-500': stat.trend === 'up',
                      'text-pink-500': stat.trend === 'down',
                      'text-600': stat.trend !== 'up' && stat.trend !== 'down'
                    }"
                  >
                    {{ (stat.current ?? 0) - (stat.previous ?? 0) | currency: 'R' }}
                  </span>
                  <span class="text-500 text-xs">vs last period</span>
                </div>
              </div>
            </div>
            <div class="col-12 md:col-4" *ngIf="orderStats.total_nrc_outstanding as stat">
              <div class="surface-100 p-3 border-round border-1 border-200 h-full">
                <div class="flex align-items-center gap-2 mb-2">
                  <i class="pi pi-file text-600"></i>
                  <span class="text-600 text-sm">NRC Outstanding</span>
                </div>
                <div class="text-900 font-bold text-xl mb-2">
                  {{ stat.current ?? 0 | currency: 'R' }}
                </div>
                <div class="flex align-items-center gap-1">
                  <i [ngClass]="getTrendIconClass(stat.trend)" class="text-sm"></i>
                  <span
                    class="text-sm font-medium"
                    [ngClass]="{
                      'text-green-500': stat.trend === 'up',
                      'text-pink-500': stat.trend === 'down',
                      'text-600': stat.trend !== 'up' && stat.trend !== 'down'
                    }"
                  >
                    {{ (stat.current ?? 0) - (stat.previous ?? 0) | currency: 'R' }}
                  </span>
                  <span class="text-500 text-xs">vs last period</span>
                </div>
              </div>
            </div>
          </div>
          <ng-template #noOrderStats>
            <p class="text-600 text-center py-3 m-0">No order data available</p>
          </ng-template>
        </ng-container>
      </p-tabPanel>

      <p-tabPanel header="CRM" *ngIf="canSeeCrmStats">
        <ng-container *ngIf="!crmLoading; else statsSkeleton">
          <div class="grid" *ngIf="leadStats; else noCrmStats">
            <div class="col-6 md:col-4 lg:col-2p4">
              <div class="surface-50 border-1 border-200 border-round p-3 text-center">
                <div class="text-600 text-sm mb-1">New</div>
                <div class="text-900 font-bold text-xl">{{ leadStats.new ?? 0 }}</div>
              </div>
            </div>
            <div class="col-6 md:col-4 lg:col-2p4">
              <div class="surface-50 border-1 border-200 border-round p-3 text-center">
                <div class="text-600 text-sm mb-1">Qualified</div>
                <div class="text-900 font-bold text-xl">{{ leadStats.qualified ?? 0 }}</div>
              </div>
            </div>
            <div class="col-6 md:col-4 lg:col-2p4">
              <div class="surface-50 border-1 border-200 border-round p-3 text-center">
                <div class="text-600 text-sm mb-1">Opportunity</div>
                <div class="text-900 font-bold text-xl">{{ leadStats.opportunity ?? 0 }}</div>
              </div>
            </div>
            <div class="col-6 md:col-4 lg:col-2p4">
              <div class="surface-50 border-1 border-200 border-round p-3 text-center">
                <div class="text-600 text-sm mb-1">Closed-Win</div>
                <div class="text-green-600 font-bold text-xl">
                  {{ leadStats['closed-win'] ?? 0 }}
                </div>
              </div>
            </div>
            <div class="col-6 md:col-4 lg:col-2p4">
              <div class="surface-50 border-1 border-200 border-round p-3 text-center">
                <div class="text-600 text-sm mb-1">Closed-Loss</div>
                <div class="text-red-500 font-bold text-xl">
                  {{ leadStats['closed-loss'] ?? 0 }}
                </div>
              </div>
            </div>
          </div>
          <ng-template #noCrmStats>
            <p class="text-600 text-center py-3 m-0">No CRM data available</p>
          </ng-template>
        </ng-container>
      </p-tabPanel>
    </p-tabView>
  </share-section-card>

  <app-user-account-profile></app-user-account-profile>

  <ng-template #statsSkeleton>
    <div class="flex flex-column gap-2">
      <p-skeleton height="2.75rem" borderRadius="8px"></p-skeleton>
      <p-skeleton height="2.75rem" borderRadius="8px"></p-skeleton>
      <p-skeleton height="2.75rem" borderRadius="8px"></p-skeleton>
    </div>
  </ng-template>
</div>
